"use client";

import { useState, useSyncExternalStore } from "react";
import Link from "next/link";
import {
  BookOpen,
  Briefcase,
  Calculator,
  ChevronDown,
  FileText,
  HelpCircle,
  Layers,
  Menu,
  Plug,
  Route,
  Scale,
  Shield,
  TrendingUp,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { LeadCaptureModal } from "@/components/lead-capture-modal";
import { useSignedInHint } from "@/hooks/use-signed-in-hint";

type MenuItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  description: string;
  badge?: string;
};

const HOME_SECTIONS: MenuItem[] = [
  {
    label: "How It Works",
    href: "/#how",
    icon: Route,
    description: "From your first call to live agents, set up for you.",
  },
  {
    label: "Use Cases",
    href: "/#usecases",
    icon: Layers,
    description: "What your AI agents handle, by industry.",
  },
  {
    label: "Why Raileon",
    href: "/#compare",
    icon: TrendingUp,
    description: "Compared to hiring, agencies, and DIY.",
  },
  {
    label: "Security",
    href: "/security",
    icon: Shield,
    description: "Encrypted in transit and at rest, isolated per customer.",
  },
  {
    label: "FAQ",
    href: "/#faq",
    icon: HelpCircle,
    description: "Common questions, answered briefly.",
  },
];

const RESOURCES: MenuItem[] = [
  {
    label: "ROI Calculator",
    href: "/roi",
    icon: Calculator,
    description: "See how much your AI workforce would save.",
    badge: "New",
  },
  {
    label: "Blog",
    href: "/blog",
    icon: FileText,
    description: "Tactical guides on automating real workflows.",
  },
  {
    label: "Documentation",
    href: "/docs",
    icon: BookOpen,
    description: "Guides for setup, integrations, and daily use.",
  },
  {
    label: "Integrations",
    href: "/integrations",
    icon: Plug,
    description: "Every app Raileon connects to, with honest status.",
  },
  {
    label: "Raileon vs Hiring In-House",
    href: "/compare/hiring-in-house",
    icon: Scale,
    description: "Cost, setup time, and availability, compared.",
  },
];

const COMPANY: MenuItem[] = [
  {
    label: "About",
    href: "/about",
    icon: Users,
    description: "Who's building Raileon and why.",
  },
  {
    label: "Careers",
    href: "/careers",
    icon: Briefcase,
    description: "We're hiring. Join the team.",
  },
];

function MenuItemRow({ item }: { item: MenuItem }) {
  const Icon = item.icon;
  return (
    <NavigationMenuLink asChild>
      <Link
        href={item.href}
        className="hover:bg-rl-bg3 group flex items-start gap-3 rounded-xl p-3 transition-colors"
      >
        <span className="bg-rl-surface/80 text-rl-accent ring-rl-accent/25 shadow-rl-accent/10 group-hover:bg-rl-accent-soft group-hover:text-rl-accent-bright group-hover:ring-rl-accent/60 group-hover:shadow-rl-accent/40 mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-xl shadow-md ring-1 transition-all duration-300 ease-out ring-inset group-hover:-translate-y-0.5 group-hover:shadow-lg motion-reduce:transition-none motion-reduce:group-hover:translate-y-0">
          <Icon
            size={16}
            aria-hidden
            className="drop-shadow-[0_0_5px_oklch(0.72_0.192_161/0.45)] transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transform-none"
          />
        </span>
        <div className="min-w-0">
          <div className="text-foreground flex items-center gap-1.5 text-[0.8125rem] font-semibold">
            {item.label}
            {item.badge && (
              <span className="bg-rl-accent/15 text-rl-accent rounded px-1.5 py-[1px] text-[0.625rem] font-extrabold tracking-wider uppercase">
                {item.badge}
              </span>
            )}
          </div>
          <p className="text-rl-text3 mt-0.5 text-[0.75rem] leading-snug">
            {item.description}
          </p>
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function NavTriggerStyle() {
  return "text-rl-text2 hover:text-foreground hover:bg-rl-surface/60 data-[state=open]:bg-rl-surface/60 data-[state=open]:text-foreground rounded-full bg-transparent px-3 py-1.5 text-[0.8125rem] font-medium";
}

function NavContentStyle() {
  return "border-rl-border bg-rl-bg2 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl";
}

function DesktopNav() {
  return (
    <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-0.5">
        <NavigationMenuItem>
          <NavigationMenuTrigger className={NavTriggerStyle()}>
            Home
          </NavigationMenuTrigger>
          <NavigationMenuContent
            className={cn(NavContentStyle(), "md:w-[480px]")}
          >
            <div className="grid grid-cols-2 gap-1">
              {HOME_SECTIONS.map((section) => (
                <MenuItemRow key={section.href} item={section} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              href="/solutions"
              className="text-rl-text2 hover:text-foreground hover:bg-rl-surface/60 inline-flex h-9 items-center rounded-full px-3 py-1.5 text-[0.8125rem] font-medium transition-colors"
            >
              Solutions
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              href="/pricing"
              className="text-rl-text2 hover:text-foreground hover:bg-rl-surface/60 inline-flex h-9 items-center rounded-full px-3 py-1.5 text-[0.8125rem] font-medium transition-colors"
            >
              Pricing
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className={NavTriggerStyle()}>
            Resources
          </NavigationMenuTrigger>
          <NavigationMenuContent
            className={cn(NavContentStyle(), "md:w-[360px]")}
          >
            <div className="grid grid-cols-1 gap-1">
              {RESOURCES.map((item) => (
                <MenuItemRow key={item.href} item={item} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className={NavTriggerStyle()}>
            Company
          </NavigationMenuTrigger>
          <NavigationMenuContent
            className={cn(NavContentStyle(), "md:w-[320px]")}
          >
            <div className="grid grid-cols-1 gap-1">
              {COMPANY.map((item) => (
                <MenuItemRow key={item.href} item={item} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function MobileNavGroup({
  label,
  rootHref,
  items,
  onClose,
}: {
  label: string;
  rootHref?: string;
  items: MenuItem[];
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-rl-border border-b">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="text-foreground hover:text-rl-accent flex w-full items-center justify-between py-4 text-base font-medium transition-colors"
      >
        {rootHref ? (
          <Link
            href={rootHref}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-1 text-left"
          >
            {label}
          </Link>
        ) : (
          <span className="flex-1 text-left">{label}</span>
        )}
        <ChevronDown
          size={16}
          className={cn(
            "text-rl-text3 ml-2 transition-transform",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-col gap-1 pb-3">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={onClose}
                className="text-rl-text2 hover:text-foreground hover:bg-rl-bg3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[0.875rem] transition-colors"
              >
                <Icon
                  size={14}
                  className="text-rl-accent shrink-0"
                  aria-hidden
                />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="bg-rl-accent/15 text-rl-accent rounded px-1.5 py-[1px] text-[0.625rem] font-extrabold tracking-wider uppercase">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileNav({
  onDemoClick,
  signedIn,
}: {
  onDemoClick: () => void;
  signedIn: boolean;
}) {
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="text-rl-text2 hover:text-foreground hover:bg-rl-surface focus-visible:ring-rl-accent flex size-11 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:outline-none lg:hidden"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
      </SheetTrigger>

      <SheetContent
        side="top"
        className="border-rl-border bg-rl-bg2/95 h-auto border-b p-0 backdrop-blur-2xl"
      >
        <SheetTitle className="sr-only">Site navigation</SheetTitle>

        <div className="px-5 pt-5 pb-6">
          <div className="mb-5 flex items-center justify-between">
            <Link
              href="/"
              onClick={close}
              className="text-foreground text-[1.4rem] font-extrabold tracking-tight"
            >
              raileon<span className="text-rl-accent">.</span>
            </Link>
            <button
              type="button"
              onClick={close}
              aria-label="Close menu"
              className="text-rl-text2 hover:text-foreground hover:bg-rl-surface flex size-9 items-center justify-center rounded-full transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="flex flex-col">
            <MobileNavGroup
              label="Home"
              rootHref="/"
              items={HOME_SECTIONS}
              onClose={close}
            />

            <Link
              href="/solutions"
              onClick={close}
              className="text-foreground hover:text-rl-accent border-rl-border border-b py-4 text-base font-medium transition-colors"
            >
              Solutions
            </Link>

            <Link
              href="/pricing"
              onClick={close}
              className="text-foreground hover:text-rl-accent border-rl-border border-b py-4 text-base font-medium transition-colors"
            >
              Pricing
            </Link>

            <MobileNavGroup
              label="Resources"
              items={RESOURCES}
              onClose={close}
            />

            <MobileNavGroup label="Company" items={COMPANY} onClose={close} />
          </nav>

          <div className="mt-5 flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => {
                close();
                onDemoClick();
              }}
              className="text-foreground border-rl-border2 hover:border-rl-text3 w-full cursor-pointer rounded-full border py-3 text-sm font-semibold transition-colors"
            >
              Book a Demo
            </button>
            {!signedIn ? (
              <Link
                href="/sign-in"
                onClick={close}
                className="text-rl-text2 hover:text-foreground w-full py-2 text-center text-sm font-semibold transition-colors"
              >
                Sign in
              </Link>
            ) : null}
            {/* Accounts are invite-only, so signed-out visitors get the
                inquiry form rather than a sign-up page. */}
            {signedIn ? (
              <Link
                href="/dashboard"
                onClick={close}
                className="bg-rl-accent hover:bg-rl-accent-bright w-full rounded-full py-3 text-center text-sm font-bold text-[#08090c] transition-colors"
              >
                Dashboard
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  close();
                  onDemoClick();
                }}
                className="bg-rl-accent hover:bg-rl-accent-bright w-full cursor-pointer rounded-full py-3 text-center text-sm font-bold text-[#08090c] transition-colors"
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Scroll position is a browser-only external store — useSyncExternalStore
// gives us a separate server snapshot (not scrolled) that hydrates cleanly,
// without the extra mount-effect render.
function subscribeToScroll(onChange: () => void) {
  window.addEventListener("scroll", onChange, { passive: true });
  return () => window.removeEventListener("scroll", onChange);
}
const getScrolledSnapshot = () => window.scrollY > 8;
const getScrolledServerSnapshot = () => false;

export function Header() {
  const scrolled = useSyncExternalStore(
    subscribeToScroll,
    getScrolledSnapshot,
    getScrolledServerSnapshot,
  );
  const signedIn = useSignedInHint();
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <header
      className={cn(
        "fixed top-0 right-0 left-0 z-50 transition-all duration-300",
        scrolled ? "pt-3 md:pt-4" : "pt-4 md:pt-6",
      )}
    >
      <div className="mx-auto max-w-280 px-4">
        <nav
          className={cn(
            "border-rl-border relative flex h-14 items-center justify-between rounded-full border px-2 pl-5 transition-all duration-300",
            scrolled
              ? "bg-rl-bg2/85 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_8px_24px_-8px_rgba(0,0,0,0.4)] backdrop-blur-xl"
              : "bg-rl-bg2/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-md",
          )}
        >
          <Link
            href="/"
            className="text-foreground text-[1.25rem] font-extrabold tracking-[-0.03em]"
          >
            raileon<span className="text-rl-accent">.</span>
          </Link>

          <div className="absolute left-1/2 -translate-x-1/2">
            <DesktopNav />
          </div>

          {/* The centered nav plus these buttons need ~900px, so the nav (and the
              desktop buttons) start at lg; "Book a Demo" opens the same form as
              "Get Started" and only joins from 1360px, where it has room (at
              1280px it sat 11px from Company). */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="text-foreground hover:bg-rl-surface hidden cursor-pointer rounded-full px-4 py-2 text-[0.8125rem] font-semibold transition-colors min-[1360px]:block"
            >
              Book a Demo
            </button>

            {!signedIn ? (
              <Link
                href="/sign-in"
                className="text-rl-text2 hover:text-foreground hidden rounded-full px-3 py-2 text-[0.8125rem] font-semibold transition-colors lg:block"
              >
                Sign in
              </Link>
            ) : null}

            {signedIn ? (
              <Link
                href="/dashboard"
                className="bg-rl-accent hover:bg-rl-accent-bright hidden rounded-full px-4 py-2 text-[0.8125rem] font-bold text-[#08090c] transition-colors lg:block"
              >
                Dashboard
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="bg-rl-accent hover:bg-rl-accent-bright hidden cursor-pointer rounded-full px-4 py-2 text-[0.8125rem] font-bold text-[#08090c] transition-colors lg:block"
              >
                Get Started
              </button>
            )}

            <MobileNav
              onDemoClick={() => setDemoOpen(true)}
              signedIn={signedIn}
            />
          </div>
        </nav>
      </div>
      <LeadCaptureModal open={demoOpen} onOpenChange={setDemoOpen} />
    </header>
  );
}
